import { Check } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  SortableTableHead,
} from '@/components/ui/table'
import type {
  PortalRegistrationPeople,
  PortalRegistrationPerson,
} from './portalEventRegistrations.types'

export function PortalEventPeopleTable({
  people,
  started,
  sort,
  onSort,
}: {
  people: PortalRegistrationPeople
  started: boolean
  sort: string
  onSort: (value: string) => void
}) {
  const [key, direction] = sort.split(':')
  const columns = [
    ['person', 'Person'],
    ['ticket', 'Ticket'],
    ['ordered', 'Ordered'],
    ...(started ? [['checked_in', 'Checked in']] : []),
    ...(people.has_previous ? [['last_year', 'Last year']] : []),
  ]
  const booked = people.rows.filter((row) => row.booked_this_year)
  const notBooked = people.has_previous ? people.rows.filter((row) => !row.booked_this_year) : []
  const renderRows = (rows: PortalRegistrationPerson[]) =>
    rows.map((row, index) => (
      <PortalPersonRow
        key={`${row.kind}:${index}`}
        row={row}
        started={started}
        previous={people.has_previous}
      />
    ))
  const widths =
    columns.length === 5
      ? ['w-[22%]', 'w-[24%]', 'w-[21%]', 'w-[16%]', 'w-[17%]']
      : columns.length === 4
        ? ['w-[30%]', 'w-[28%]', 'w-[24%]', 'w-[18%]']
        : ['w-[36%]', 'w-[34%]', 'w-[30%]']
  const compactLabels: Record<string, string> = {
    ordered: 'Date',
    checked_in: 'In',
    last_year: 'Prior',
  }
  return (
    <Table aria-label="Organization people" className="table-fixed">
      <colgroup>
        {widths.map((width, index) => (
          <col key={index} className={`${width} sm:w-auto`} />
        ))}
      </colgroup>
      <TableHeader>
        <TableRow>
          {columns.map(([id, label]) => (
            <SortableTableHead
              key={id}
              className="whitespace-nowrap px-1 sm:px-3 [&_button]:whitespace-nowrap [&_svg]:shrink-0"
              sortLabel={label}
              aria-label={label}
              title={label}
              active={key === id}
              direction={direction === 'desc' ? 'descending' : 'ascending'}
              onToggle={() => onSort(`${id}:${key === id && direction === 'asc' ? 'desc' : 'asc'}`)}
            >
              {compactLabels[id] ? (
                <>
                  <span className="sm:hidden" aria-hidden="true">
                    {compactLabels[id]}
                  </span>
                  <span className="hidden sm:inline" aria-hidden="true">
                    {label}
                  </span>
                </>
              ) : (
                label
              )}
            </SortableTableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {renderRows(booked)}
        {notBooked.length > 0 && (
          <>
            <TableRow className="bg-muted/40 hover:bg-muted/40">
              <TableCell
                colSpan={columns.length}
                className="h-9 py-0 text-caption font-medium text-muted-foreground"
              >
                Not booked this year · {notBooked.length}
              </TableCell>
            </TableRow>
            {renderRows(notBooked)}
          </>
        )}
      </TableBody>
    </Table>
  )
}
function PortalPersonRow({
  row,
  started,
  previous,
}: {
  row: PortalRegistrationPerson
  started: boolean
  previous: boolean
}) {
  const name =
    row.kind === 'erased' ? 'Erased contact' : row.kind === 'unnamed' ? 'Awaiting name' : row.name
  return (
    <TableRow>
      <TableCell className="break-words px-1 sm:px-3">
        <span className={row.kind !== 'named' ? 'text-muted-foreground' : undefined}>{name}</span>
        {row.former && row.kind === 'named' && (
          <Badge variant="neutral" className="ml-1">
            Former
          </Badge>
        )}
      </TableCell>
      <TableCell className="break-words px-1 sm:px-3">{row.ticket_name ?? '—'}</TableCell>
      <TableCell className="px-1 sm:px-3">{row.ordered_label}</TableCell>
      {started && (
        <TableCell className="px-1 sm:px-3">
          {row.checked_in ? (
            <Check role="img" aria-label="Checked in" className="size-4 text-success" />
          ) : (
            '—'
          )}
        </TableCell>
      )}
      {previous && (
        <TableCell className="px-1 sm:px-3">
          {row.held_last_year ? (
            <Check role="img" aria-label="Held a place last year" className="size-4 text-success" />
          ) : (
            '—'
          )}
        </TableCell>
      )}
    </TableRow>
  )
}
